import { createContext, useContext, type ReactNode } from 'react'
import { atLeast, type Role } from '../shared/roles'
import type { SessionResponse } from '../shared/types'

/**
 * Who is signed in and what they may do, for the screens that show controls
 * conditionally. The server is the authority — every mutation is gated there
 * too — so this only decides what to draw, never what is allowed.
 */
interface SessionValue {
  session: SessionResponse | null
  role: Role | null
  /** True when the signed-in role is `min` or above. */
  can: (min: Role) => boolean
}

const SessionContext = createContext<SessionValue>({
  session: null,
  role: null,
  can: () => false,
})

export function SessionProvider({
  session,
  children,
}: {
  session: SessionResponse | null
  children: ReactNode
}) {
  const role = session?.role ?? null
  return (
    <SessionContext.Provider value={{ session, role, can: (min) => atLeast(role, min) }}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession(): SessionValue {
  return useContext(SessionContext)
}
